import * as React from "react"
import { Link } from "@tanstack/react-router"
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { SidebarTrigger } from "@/components/ui/sidebar"

export interface BreadcrumbItemType {
  title: string
  href?: string
  isEllipsis?: boolean
}

export interface SidebarTopBarProps {
  breadcrumbs: BreadcrumbItemType[]
  showEllipsis?: boolean
  onToggleSidebar?: () => void
}

export function SidebarTopBarComponent({ breadcrumbs, showEllipsis = false, onToggleSidebar }: SidebarTopBarProps) {
  const visibleBreadcrumbs = React.useMemo(() => {
    if (!showEllipsis || breadcrumbs.length <= 3) {
      return breadcrumbs
    }

    return [
      breadcrumbs[0],
      { title: "...", isEllipsis: true },
      ...breadcrumbs.slice(-2),
    ]
  }, [breadcrumbs, showEllipsis])

  const lastIndex = breadcrumbs.length - 1

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border-default px-4">
      <SidebarTrigger onClick={onToggleSidebar} className="h-8 w-8 min-h-8 min-w-8 rounded-lg p-0" />
      <div className="hidden h-9 min-h-9 items-center gap-2 px-3 md:flex">
        <Breadcrumb>
          <BreadcrumbList className="gap-2">
            {visibleBreadcrumbs.map((item, index) => {
              const isLast = index === visibleBreadcrumbs.length - 1
              const originalIndex = breadcrumbs.indexOf(item)
              const isActive = originalIndex === lastIndex

              return (
                <React.Fragment key={index}>
                  <BreadcrumbItem className={index < visibleBreadcrumbs.length - 2 ? "hidden md:flex" : "flex"}>
                    {item.isEllipsis ? (
                      <BreadcrumbEllipsis className="h-4 w-4" />
                    ) : isActive ? (
                      <BreadcrumbPage className="text-sm font-semibold leading-[21px] tracking-[0.07px] text-text-default">
                        {item.title}
                      </BreadcrumbPage>
                    ) : item.href ? (
                      <BreadcrumbLink asChild className="text-sm font-normal leading-[21px] tracking-[0.07px] text-text-subtlest hover:text-text-subtle">
                        <Link to={item.href}>{item.title}</Link>
                      </BreadcrumbLink>
                    ) : (
                      <span className="text-sm font-normal leading-[21px] tracking-[0.07px] text-text-subtlest">
                        {item.title}
                      </span>
                    )}
                  </BreadcrumbItem>
                  {!isLast && (
                    <BreadcrumbSeparator className={index < visibleBreadcrumbs.length - 2 ? "hidden md:block" : ""} />
                  )}
                </React.Fragment>
              )
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </header>
  )
}
